import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { ChartArea, Download, Info, Loader2, MapPin, Maximize2, Minimize2 } from 'lucide-react';
import { useTerminalTelemetry } from '@/domains/terminals/services/account-user-terminals';
import { ChartContainer, ChartTooltip } from '@/shared/ui/chart';
import { XAxis, YAxis, CartesianGrid, Area, AreaChart } from 'recharts';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { TerminalTelemetry } from '@/shared/types/userTerminalTypes';
import { MetricType, formatMetricValue } from '@/shared/utils/format-utils';
import { exportToCsv } from '@/shared/utils/csv-export-utils';
import { cn, formatDateToLocaleString } from '@/shared/utils/date-utils';
import EmptyView from '@/shared/components/EmptyView';

export interface TelemetryDataPoint {
  timestamp: string;
  value: number;
}

interface H3CellTelemetryPoint {
  timestamp: string;
  cellId: string;
}

interface TelemetryMetrics {
  downlinkThroughput: TelemetryDataPoint[];
  uplinkThroughput: TelemetryDataPoint[];
  latency: TelemetryDataPoint[];
  pingDropRate: TelemetryDataPoint[];
  signalQuality: TelemetryDataPoint[];
  obstruction: TelemetryDataPoint[];
  h3CellIds: H3CellTelemetryPoint[];
}

interface TerminalTelemetrySectionProps {
  terminalId: string;
}

/** Time range options for the terminal telemetry section */
type TimeRangeOption = {
  value: string;
  label: string;
  timeFormat: string;
};

/** Time range options for the terminal telemetry section */
const timeRangeOptions: TimeRangeOption[] = [
  { value: '15m', label: '15 Minutes', timeFormat: 'h:mm a' },
  { value: '1h', label: '1 Hour', timeFormat: 'h:mm a' },
  { value: '3h', label: '3 Hours', timeFormat: 'h:mm a' },
  { value: '1d', label: '1 Day', timeFormat: 'MMM d, h:mm a' },
  { value: '7d', label: '7 Days', timeFormat: 'MMM d, h:mm a' },
  { value: '30d', label: '30 Days', timeFormat: 'MMM d, h:mm a' },
];

function calculateTelemetryStats(data: TelemetryDataPoint[], metricType: MetricType) {
  if (!data || data.length === 0) {
    return { min: '0', max: '0', last: '0' };
  }

  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const last = values[values.length - 1] || 0;

  return {
    min: formatMetricValue(min, metricType),
    max: formatMetricValue(max, metricType),
    last: formatMetricValue(last, metricType),
  };
}

type TelemetryMetricCardProps = {
  title: string;
  description: string;
  data: TelemetryDataPoint[];
  unit: string;
  metricType?: MetricType;
  gradientId: string;
  timeRange: string;
  isLoading: boolean;
};

function TelemetryMetricCard({
  title,
  description,
  data,
  metricType,
  gradientId,
  timeRange,
  isLoading,
}: TelemetryMetricCardProps) {
  const stats = calculateTelemetryStats(data, metricType ?? MetricType.throughput);
  const chartData = data.map((d) => ({
    timestamp: d.timestamp,
    time: format(new Date(d.timestamp), timeRangeOptions.find((option) => option.value === timeRange)?.timeFormat ?? 'h:mm a'),
    value: d.value,
  }));

  return (
    <Card>
      <CardHeader className="p-2 bg-muted rounded-t-lg">
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-row items-center">
            <CardTitle className="text-base font-medium flex flex-row items-center gap-2">
              {title}
            </CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full hover:bg-muted">
                  <Info className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{description}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-sm flex flex-row items-center gap-2">
            <span>Min {stats.min} · Max {stats.max} · Last {stats.last}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 px-2 py-2 h-48 items-center justify-center bg-background rounded-b-lg">
        {chartData.length > 0 ? (
          <ChartContainer
            config={{
              value: {
                label: title,
                color: 'hsl(var(--foreground))',
              },
            }}
            className="w-full h-full"
            style={{ height: '100%' }}
          >
            <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="hsl(var(--foreground))"
                    stopOpacity={0.25}
                  />
                  <stop
                    offset="95%"
                    stopColor="hsl(var(--foreground))"
                    stopOpacity={0.03}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.2} />
              <XAxis dataKey="time" tick={{ fontSize: 9 }} />
              <YAxis
                tick={{ fontSize: 9 }}
                tickFormatter={(v) => formatMetricValue(v, metricType ?? MetricType.throughput)}
                domain={[0, 'auto']}
                hide
              />
              <ChartTooltip
                cursor={{ stroke: 'hsl(var(--foreground))', strokeDasharray: '4 4' }}
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="text-xs">{label}</div>
                        <div className="font-medium flex items-center gap-2">
                          <span className="w-2 h-0.5 bg-foreground shrink-0" />
                          {title}: {formatMetricValue(Number(payload[0]?.value ?? 0), metricType ?? MetricType.throughput)}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--foreground))"
                strokeWidth={2}
                fill={`url(#${gradientId})`}
              />
            </AreaChart>
          </ChartContainer>
        ) : (
          isLoading ? (
            <div className="flex items-center justify-center text-xs gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading telemetry data...
            </div>
          ) : (
            <EmptyView
              icon={ChartArea}
              size="small"
              title="No Telemetry Data Found"
              description="This terminal doesn't have any telemetry data yet."
            />
          )
        )}
      </CardContent>
    </Card>
  );
}

const TerminalTelemetrySection = ({ terminalId }: TerminalTelemetrySectionProps) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('15m');
  const [isExpandedCharts, setIsExpandedCharts] = useState(false);

  // Fetch telemetry data from API
  const { data: telemetryRawData, isLoading, error } = useTerminalTelemetry(terminalId, selectedTimeRange);

  const sortedTelemetryRawData = useMemo(() => {
    return telemetryRawData?.sort((a, b) => new Date(a.utcTimestamp ?? '').getTime() - new Date(b.utcTimestamp ?? '').getTime());
  }, [telemetryRawData]);

  // Transform raw API data to the expected format
  const transformTelemetryData = (rawData: TerminalTelemetry[]): TelemetryMetrics => {
    if (!rawData || rawData.length === 0) {
      return {
        downlinkThroughput: [],
        uplinkThroughput: [],
        latency: [],
        pingDropRate: [],
        signalQuality: [],
        obstruction: [],
        h3CellIds: []
      };
    }

    const metrics: TelemetryMetrics = {
      downlinkThroughput: [],
      uplinkThroughput: [],
      latency: [],
      pingDropRate: [],
      signalQuality: [],
      obstruction: [],
      h3CellIds: []
    };

    rawData.forEach((point) => {
      const timestamp: string = point.utcTimestamp ?? '';
      if (point.downlinkThroughput !== undefined) {
        metrics.downlinkThroughput.push({ timestamp, value: Math.round(point.downlinkThroughput * 1000) });
      }
      if (point.uplinkThroughput !== undefined) {
        metrics.uplinkThroughput.push({ timestamp, value: Math.round(point.uplinkThroughput * 1000) });
      }
      if (point.pingLatencyMsAvg !== undefined) {
        metrics.latency.push({ timestamp, value: point.pingLatencyMsAvg });
      }
      if (point.pingDropRateAvg !== undefined) {
        metrics.pingDropRate.push({ timestamp, value: Math.round(point.pingDropRateAvg * 100) });
      }
      if (point.signalQuality !== undefined) {
        metrics.signalQuality.push({ timestamp, value: Math.round(point.signalQuality * 100) });
      }
      if (point.obstructionPercentTime !== undefined) {
        metrics.obstruction.push({ timestamp, value: point.obstructionPercentTime });
      }
      if (point.h3CellId != null && point.h3CellId !== "") {
        metrics.h3CellIds.push({ timestamp, cellId: point.h3CellId });
      }
    });

    return metrics;
  };

  const telemetryData = transformTelemetryData(sortedTelemetryRawData);

  const lastTelemetryTimestamp = sortedTelemetryRawData && sortedTelemetryRawData.length > 0 ? sortedTelemetryRawData[sortedTelemetryRawData.length - 1]?.utcTimestamp ?? undefined : undefined;

  const handleExport = () => {
    // Transform raw data to CSV format
    const csvData = sortedTelemetryRawData.map(point => {
      return {
        'timestamp': point.utcTimestamp || '-',
        'downlink_throughput_kbps': point.downlinkThroughput ? Math.round(point.downlinkThroughput * 1000) : 0,
        'uplink_throughput_kbps': point.uplinkThroughput ? Math.round(point.uplinkThroughput * 1000) : 0,
        'ping_latency_ms_avg': point.pingLatencyMsAvg ? Math.round(point.pingLatencyMsAvg) : 0,
        'ping_drop_rate_avg_percentage': point.pingDropRateAvg ? Math.round(point.pingDropRateAvg * 100) : 0,
        'signal_quality_avg_percentage': point.signalQuality ? Math.round(point.signalQuality * 100) : 100, // Default to 100 if not available
        'obstruction_percentage': point.obstructionPercentTime ? point.obstructionPercentTime.toFixed(3) : '0.000',
        'h3_cell_id': point.h3CellId ? point.h3CellId : '-'
      };
    });

    exportToCsv(csvData, `terminal_telemetry_${terminalId}_${selectedTimeRange}`);
  };

  return (
    <div className="flex flex-col w-full border rounded-lg">
      <div className="flex flex-row items-center justify-between gap-2 bg-muted p-4 rounded-t-md">
        <div className="flex flex-row items-center justify-start gap-2">
          <ChartArea className="h-4 w-4 text-primary" />
          <h3 className="text-lg font-semibold">Telemetry</h3>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          {/* Timestamp of the last telemetry data */}
          {lastTelemetryTimestamp && (
            <p className="text-sm text-muted-foreground">Last updated: {formatDateToLocaleString(lastTelemetryTimestamp)}</p>
          )}
          <Button variant="outline" size="sm" onClick={() => setIsExpandedCharts(!isExpandedCharts)} disabled={isLoading}>
            {isExpandedCharts ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            {isExpandedCharts ? 'Collapse Charts' : 'Expand Charts'}
          </Button>
        </div>
      </div>
      <div className="space-y-4 p-4">
        <div className="flex flex-row items-center justify-between gap-2">
          {/* Time Range Selector */}
          <div className="flex flex-row items-center justify-middle gap-2">
            {timeRangeOptions.map((option) => (
              <Button
                key={option.value}
                variant="outline"
                className={cn(
                  selectedTimeRange === option.value ? "bg-foreground text-background border-foreground hover:bg-foreground hover:text-background" : "bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
                )}
                size="sm"
                onClick={() => setSelectedTimeRange(option.value)}
                disabled={isLoading}
              >
                {option.label}
              </Button>
            ))}
          </div>
          {/* Export and Expand/Collapse Buttons */}
          <div className="flex flex-row items-center justify-end gap-2">
            {sortedTelemetryRawData?.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleExport} disabled={isLoading}>
                <Download className="w-4 h-4" />
                Export
              </Button>
            )}
          </div>
        </div>
        {/* Metrics Grid */}
        <div className={`grid ${isExpandedCharts ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'} gap-4`}>
          <TelemetryMetricCard
            title="Downlink Throughput"
            description="Average download speed"
            data={telemetryData.downlinkThroughput}
            unit=""
            metricType={MetricType.throughput}
            gradientId="downlink-gradient"
            timeRange={selectedTimeRange}
            isLoading={isLoading}
          />

          <TelemetryMetricCard
            title="Uplink Throughput"
            description="Average upload speed"
            data={telemetryData.uplinkThroughput}
            unit=""
            metricType={MetricType.throughput}
            gradientId="uplink-gradient"
            timeRange={selectedTimeRange}
            isLoading={isLoading}
          />

          <TelemetryMetricCard
            title="Latency"
            description="Average latency measured to the Starlink point of presence"
            data={telemetryData.latency}
            unit=" ms"
            metricType={MetricType.latency}
            gradientId="latency-gradient"
            timeRange={selectedTimeRange}
            isLoading={isLoading}
          />

          <TelemetryMetricCard
            title="Ping Drop Rate"
            description="Percentage of pings that were dropped"
            data={telemetryData.pingDropRate}
            unit="%"
            metricType={MetricType.percentage}
            gradientId="ping-drop-gradient"
            timeRange={selectedTimeRange}
            isLoading={isLoading}
          />

          <TelemetryMetricCard
            title="Signal Quality"
            description="Signal to noise ratio (SNR) expressed as a percent from 0 dB to 10 dB"
            data={telemetryData.signalQuality}
            unit="%"
            metricType={MetricType.percentage}
            gradientId="signal-quality-gradient"
            timeRange={selectedTimeRange}
            isLoading={isLoading}
          />

          <TelemetryMetricCard
            title="Obstruction"
            description="Percent of time the dish is obstructed"
            data={telemetryData.obstruction}
            unit="%"
            metricType={MetricType.obstruction}
            gradientId="obstruction-gradient"
            timeRange={selectedTimeRange}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default TerminalTelemetrySection; 